import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Zap, Send, Loader2, Bot, User, Check, Trash2, Pencil, Filter, Clock, FolderOpen, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ZeusAgentTasks from "@/components/zeus-agent-tasks";
import ZeusGuardrails from "@/components/zeus-guardrails";
import TalkToZeus from "@/components/talk-to-zeus";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

const TIMINGS = ["hourly", "morning", "afternoon", "evening", "daily", "weekly", "monthly", "quarterly", "semi-annual", "annually"] as const;
type Timing = (typeof TIMINGS)[number];

interface ZeusRule {
  id: string;
  name: string;
  ruleText: string;
  category: string;
  timing: Timing;
  status: "active" | "paused";
  contactId: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ContactOption {
  id: string;
  firstName: string;
  lastName: string;
  businessName: string | null;
}

function contactOptionName(c: ContactOption): string {
  const name = [c.firstName, c.lastName].filter(Boolean).join(" ").trim();
  return name || c.businessName || "Unnamed";
}

interface ProposedRule {
  name: string;
  ruleText: string;
  category: string;
  timing: Timing;
}

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

const TIMING_LABELS: Record<Timing, string> = {
  hourly: "Hourly",
  morning: "Morning",
  afternoon: "Afternoon",
  evening: "Evening",
  daily: "Daily",
  weekly: "Weekly",
  monthly: "Monthly",
  quarterly: "Quarterly",
  "semi-annual": "Semi-Annual",
  annually: "Annually",
};

export default function ZeusPage() {
  const { toast } = useToast();

  // ── Left column state ────────────────────────────────────────────────────
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [timingFilter, setTimingFilter] = useState<string>("all");
  const [selectedRuleId, setSelectedRuleId] = useState<string | null>(null);

  // ── Right-top state (rule detail / edit) ─────────────────────────────────
  const [editName, setEditName] = useState("");
  const [editRuleText, setEditRuleText] = useState("");
  const [editCategory, setEditCategory] = useState("");
  const [editTiming, setEditTiming] = useState<Timing>("daily");
  const [editStatus, setEditStatus] = useState<"active" | "paused">("active");
  const [editContactId, setEditContactId] = useState<string>("");
  const [detailMode, setDetailMode] = useState<"empty" | "proposal" | "rule">("empty");

  // ── Chat state ───────────────────────────────────────────────────────────
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState("");
  const [proposedRule, setProposedRule] = useState<ProposedRule | null>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);

  const { data: rules = [], isLoading: rulesLoading } = useQuery<ZeusRule[]>({
    queryKey: ["/api/zeus/rules"],
  });

  const { data: contactOptions = [] } = useQuery<ContactOption[]>({
    queryKey: ["/api/contacts"],
  });

  const categories = useMemo(
    () => Array.from(new Set(rules.map((r) => r.category).filter(Boolean))).sort(),
    [rules],
  );

  const filteredRules = useMemo(
    () =>
      rules.filter(
        (r) =>
          (categoryFilter === "all" || r.category === categoryFilter) &&
          (timingFilter === "all" || r.timing === timingFilter),
      ),
    [rules, categoryFilter, timingFilter],
  );

  const selectedRule = rules.find((r) => r.id === selectedRuleId) ?? null;

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Load a saved rule into the editor when selected
  useEffect(() => {
    if (selectedRule) {
      setEditName(selectedRule.name);
      setEditRuleText(selectedRule.ruleText);
      setEditCategory(selectedRule.category);
      setEditTiming(selectedRule.timing);
      setEditStatus(selectedRule.status);
      setEditContactId(selectedRule.contactId || "");
      setDetailMode("rule");
    }
  }, [selectedRuleId, selectedRule?.updatedAt]);

  const loadProposal = (p: ProposedRule) => {
    setSelectedRuleId(null);
    setEditName(p.name);
    setEditRuleText(p.ruleText);
    setEditCategory(p.category);
    setEditTiming(p.timing);
    setEditStatus("active");
    setEditContactId("");
    setDetailMode("proposal");
  };

  // ── Mutations ────────────────────────────────────────────────────────────
  const chatMutation = useMutation({
    mutationFn: async (message: string) => {
      const res = await apiRequest("POST", "/api/zeus/chat", {
        message,
        conversationHistory: messages,
      });
      return (await res.json()) as { response: string; proposedRule: ProposedRule | null };
    },
    onSuccess: (data) => {
      setMessages((prev) => [...prev, { role: "assistant", content: data.response }]);
      if (data.proposedRule) {
        setProposedRule(data.proposedRule);
        loadProposal(data.proposedRule);
      }
    },
    onError: (err: Error) => {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "Sorry, something went wrong. Please try again." },
      ]);
      toast({ title: "Zeus chat failed", description: err.message, variant: "destructive" });
    },
  });

  const createMutation = useMutation({
    mutationFn: async (rule: ProposedRule & { status?: "active" | "paused" }) => {
      const res = await apiRequest("POST", "/api/zeus/rules", rule);
      return (await res.json()) as ZeusRule;
    },
    onSuccess: (rule) => {
      queryClient.invalidateQueries({ queryKey: ["/api/zeus/rules"] });
      setProposedRule(null);
      setSelectedRuleId(rule.id);
      setDetailMode("rule");
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: `Rule "${rule.name}" saved under ${rule.category} (${TIMING_LABELS[rule.timing]}). You can see it in the left column, or tell me about the next rule you'd like to create.` },
      ]);
      toast({ title: "Rule saved", description: `"${rule.name}" was added to your Zeus rules.` });
    },
    onError: (err: Error) =>
      toast({ title: "Failed to save rule", description: err.message, variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: async (args: { id: string; updates: Partial<ProposedRule> & { status?: string } }) => {
      const res = await apiRequest("PATCH", `/api/zeus/rules/${args.id}`, args.updates);
      return (await res.json()) as ZeusRule;
    },
    onSuccess: (rule) => {
      queryClient.invalidateQueries({ queryKey: ["/api/zeus/rules"] });
      toast({ title: "Rule updated", description: `"${rule.name}" was updated.` });
    },
    onError: (err: Error) =>
      toast({ title: "Failed to update rule", description: err.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/zeus/rules/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/zeus/rules"] });
      setSelectedRuleId(null);
      setDetailMode("empty");
      toast({ title: "Rule deleted" });
    },
    onError: (err: Error) =>
      toast({ title: "Failed to delete rule", description: err.message, variant: "destructive" }),
  });

  const sendChat = () => {
    const text = chatInput.trim();
    if (!text || chatMutation.isPending) return;
    setMessages((prev) => [...prev, { role: "user", content: text }]);
    setChatInput("");
    chatMutation.mutate(text);
  };

  const handleSaveDetail = () => {
    if (!editName.trim() || !editRuleText.trim() || !editCategory.trim()) {
      toast({ title: "Missing fields", description: "Name, rule text, and category are required.", variant: "destructive" });
      return;
    }
    const payload = {
      name: editName.trim(),
      ruleText: editRuleText.trim(),
      category: editCategory.trim(),
      timing: editTiming,
      status: editStatus,
      contactId: editContactId || null,
    };
    if (detailMode === "rule" && selectedRule) {
      updateMutation.mutate({ id: selectedRule.id, updates: payload });
    } else if (detailMode === "proposal") {
      if (createMutation.isPending) return;
      createMutation.mutate(payload);
    }
  };

  return (
    <div className="flex h-[calc(100vh-4rem)] min-h-0 flex-col overflow-hidden bg-background text-foreground">
      {/* Header bar */}
      <div className="flex items-center gap-3 border-b border-border/60 px-5 py-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-yellow-400 to-amber-600 shadow-md shadow-yellow-500/20">
          <Zap className="h-5 w-5 text-white" style={{ fill: "white" }} />
        </div>
        <div>
          <h1 className="text-lg font-bold leading-tight">Zeus</h1>
          <p className="text-xs text-muted-foreground">Agentic rules and delegated tasks</p>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <Badge variant="secondary" className="gap-1 text-xs">
            <Sparkles className="h-3 w-3" /> AI Rule Builder
          </Badge>
          <TalkToZeus />
        </div>
      </div>

      <Tabs defaultValue="guardrails" className="flex min-h-0 flex-1 flex-col">
        <div className="border-b border-border/60 px-5 pt-2">
          <TabsList className="h-9">
            <TabsTrigger value="guardrails" data-testid="tab-zeus-guardrails">Rules</TabsTrigger>
            <TabsTrigger value="rules" data-testid="tab-zeus-automation">Automation</TabsTrigger>
            <TabsTrigger value="agent-tasks" data-testid="tab-zeus-agent-tasks">Agent Tasks</TabsTrigger>
          </TabsList>
        </div>

        {/* Wrapper gives both panels a defined pixel height via absolute positioning */}
        <div className="relative min-h-0 flex-1">
        <TabsContent value="guardrails" className="absolute inset-0 mt-0 flex flex-col overflow-hidden data-[state=inactive]:hidden">
          <ZeusGuardrails />
        </TabsContent>

        <TabsContent value="agent-tasks" className="absolute inset-0 mt-0 overflow-y-auto data-[state=inactive]:hidden">
          <ZeusAgentTasks />
        </TabsContent>

        <TabsContent value="rules" className="absolute inset-0 mt-0 flex flex-col overflow-hidden data-[state=inactive]:hidden">
      {/* Split layout */}
      <div className="flex min-h-0 flex-1">
        {/* ── LEFT COLUMN (50%): saved rules + filters ─────────────────────── */}
        <div className="flex w-1/2 min-h-0 flex-col border-r border-border/60">
          <div className="flex flex-wrap items-center gap-2 border-b border-border/40 px-4 py-3">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="h-8 w-[160px] text-xs" data-testid="select-filter-category">
                <FolderOpen className="mr-1 h-3.5 w-3.5" />
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={timingFilter} onValueChange={setTimingFilter}>
              <SelectTrigger className="h-8 w-[150px] text-xs" data-testid="select-filter-timing">
                <Clock className="mr-1 h-3.5 w-3.5" />
                <SelectValue placeholder="Timing" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All timings</SelectItem>
                {TIMINGS.map((t) => (
                  <SelectItem key={t} value={t}>{TIMING_LABELS[t]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="ml-auto text-xs text-muted-foreground">
              {filteredRules.length} rule{filteredRules.length === 1 ? "" : "s"}
            </span>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto p-4">
            {rulesLoading ? (
              <div className="flex h-full items-center justify-center text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            ) : filteredRules.length === 0 ? (
              <div className="flex h-full flex-col items-center justify-center gap-2 text-center text-muted-foreground">
                <Zap className="h-8 w-8 opacity-40" />
                <p className="text-sm font-medium">No Zeus rules yet</p>
                <p className="max-w-xs text-xs">
                  Use the chat on the bottom right to describe what you want an agent to do. Zeus will interview you and build the rule.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredRules.map((rule) => (
                  <button
                    key={rule.id}
                    onClick={() => setSelectedRuleId(rule.id)}
                    data-testid={`card-rule-${rule.id}`}
                    className={`w-full rounded-lg border p-3 text-left transition-colors hover:bg-accent/50 ${
                      selectedRuleId === rule.id ? "border-yellow-500/60 bg-yellow-500/5" : "border-border/60"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <span className="font-semibold text-sm">{rule.name}</span>
                      <Badge variant={rule.status === "active" ? "default" : "secondary"} className="shrink-0 text-[10px]">
                        {rule.status}
                      </Badge>
                    </div>
                    <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{rule.ruleText}</p>
                    <div className="mt-2 flex items-center gap-2">
                      <Badge variant="outline" className="gap-1 text-[10px]">
                        <FolderOpen className="h-3 w-3" /> {rule.category}
                      </Badge>
                      <Badge variant="outline" className="gap-1 text-[10px]">
                        <Clock className="h-3 w-3" /> {TIMING_LABELS[rule.timing] ?? rule.timing}
                      </Badge>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── RIGHT COLUMN (50%): top = rule detail, bottom = AI chat ──────── */}
        <div className="flex w-1/2 min-h-0 flex-col">
          {/* Top row: rule detail / editor */}
          <div className="flex h-1/2 min-h-0 flex-col border-b border-border/60">
            <div className="flex items-center gap-2 border-b border-border/40 px-4 py-2.5">
              <Pencil className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-semibold">
                {detailMode === "rule" ? "Rule Details" : detailMode === "proposal" ? "Proposed Rule — review & approve" : "What a Zeus rule looks like"}
              </span>
              {detailMode === "rule" && selectedRule && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="ml-auto h-7 text-destructive hover:text-destructive"
                  onClick={() => deleteMutation.mutate(selectedRule.id)}
                  disabled={deleteMutation.isPending}
                  data-testid="button-delete-rule"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto p-4">
              {detailMode === "empty" ? (
                <Card className="border-dashed">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-sm">
                      <Zap className="h-4 w-4 text-yellow-500" /> Example Zeus rule
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <p><span className="font-semibold">Name:</span> Weekly Goal Progress Digest</p>
                    <p><span className="font-semibold">Rule:</span> Every week, review all of my active goals and their tasks, summarize progress made, flag anything blocked or behind schedule, and produce a short action plan for the coming week.</p>
                    <div className="flex gap-2 pt-1">
                      <Badge variant="outline" className="gap-1 text-xs"><FolderOpen className="h-3 w-3" /> Goals</Badge>
                      <Badge variant="outline" className="gap-1 text-xs"><Clock className="h-3 w-3" /> Weekly</Badge>
                    </div>
                    <p className="pt-2 text-xs text-muted-foreground">
                      Chat with Zeus below to build your own rule. Once a rule is saved, click it in the left column to view and edit it here.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="rule-name">Name</Label>
                    <Input id="rule-name" value={editName} onChange={(e) => setEditName(e.target.value)} data-testid="input-rule-name" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="rule-text">Rule (what the agent should do)</Label>
                    <Textarea id="rule-text" rows={4} value={editRuleText} onChange={(e) => setEditRuleText(e.target.value)} data-testid="input-rule-text" />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="rule-category">Category</Label>
                      <Input id="rule-category" value={editCategory} onChange={(e) => setEditCategory(e.target.value)} list="zeus-categories" data-testid="input-rule-category" />
                      <datalist id="zeus-categories">
                        {categories.map((c) => (<option key={c} value={c} />))}
                      </datalist>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Timing</Label>
                      <Select value={editTiming} onValueChange={(v) => setEditTiming(v as Timing)}>
                        <SelectTrigger data-testid="select-rule-timing"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {TIMINGS.map((t) => (<SelectItem key={t} value={t}>{TIMING_LABELS[t]}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Status</Label>
                      <Select value={editStatus} onValueChange={(v) => setEditStatus(v as "active" | "paused")}>
                        <SelectTrigger data-testid="select-rule-status"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="paused">Paused</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Contact (optional)</Label>
                      <Select value={editContactId || "none"} onValueChange={(v) => setEditContactId(v === "none" ? "" : v)}>
                        <SelectTrigger data-testid="select-rule-contact"><SelectValue placeholder="No contact" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No contact</SelectItem>
                          {contactOptions.map((c) => (
                            <SelectItem key={c.id} value={c.id}>{contactOptionName(c)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex gap-2 pt-1">
                    <Button
                      onClick={handleSaveDetail}
                      disabled={createMutation.isPending || updateMutation.isPending}
                      className="gap-1.5"
                      data-testid="button-save-rule"
                    >
                      {(createMutation.isPending || updateMutation.isPending) ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Check className="h-4 w-4" />
                      )}
                      {detailMode === "proposal" ? "Approve & Save" : "Save Changes"}
                    </Button>
                    {detailMode === "proposal" && (
                      <Button variant="outline" onClick={() => { setDetailMode("empty"); setProposedRule(null); }} data-testid="button-discard-proposal">
                        Discard
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Bottom row: AI chat */}
          <div className="flex h-1/2 min-h-0 flex-col">
            <div className="flex items-center gap-2 border-b border-border/40 px-4 py-2.5">
              <Bot className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-semibold">Build a rule with Zeus</span>
              {proposedRule && (
                <Badge variant="secondary" className="ml-auto text-[10px]">Proposal ready — review above</Badge>
              )}
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto p-4">
              {messages.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center gap-2 text-center text-muted-foreground">
                  <Bot className="h-8 w-8 opacity-40" />
                  <p className="text-sm">Tell Zeus what you want an agent to do for you.</p>
                  <p className="max-w-sm text-xs">
                    Zeus will ask about the details, the category, and how often it should run — then propose a rule for your approval.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {messages.map((m, i) => (
                    <div key={i} className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                      {m.role === "assistant" && (
                        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-yellow-400 to-amber-600">
                          <Zap className="h-3.5 w-3.5 text-white" />
                        </div>
                      )}
                      <div
                        className={`max-w-[80%] whitespace-pre-wrap rounded-lg px-3 py-2 text-sm ${
                          m.role === "user" ? "bg-primary text-primary-foreground" : "bg-muted"
                        }`}
                      >
                        {m.content}
                      </div>
                      {m.role === "user" && (
                        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted">
                          <User className="h-3.5 w-3.5" />
                        </div>
                      )}
                    </div>
                  ))}
                  {chatMutation.isPending && (
                    <div className="flex gap-2">
                      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-yellow-400 to-amber-600">
                        <Zap className="h-3.5 w-3.5 text-white" />
                      </div>
                      <div className="rounded-lg bg-muted px-3 py-2">
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      </div>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </div>
              )}
            </div>

            <div className="border-t border-border/40 p-3">
              <div className="flex gap-2">
                <Textarea
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      sendChat();
                    }
                  }}
                  placeholder="e.g. Every morning, review my calendar and tasks and send me a prioritized plan for the day…"
                  rows={2}
                  className="min-h-0 resize-none"
                  data-testid="input-zeus-chat"
                />
                <Button
                  onClick={sendChat}
                  disabled={!chatInput.trim() || chatMutation.isPending}
                  size="icon"
                  className="h-auto shrink-0"
                  data-testid="button-send-chat"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
        </TabsContent>
        </div> {/* end relative wrapper */}
      </Tabs>
    </div>
  );
}
